import React, { useEffect } from 'react';
import { Container, Row, Col, Alert, Button, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES, buildRouteWithLang } from '../../constants/routes';

const SignIn: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const { user, isAuthenticated, isLoading, login } = useAuth();
  const [loginTriggered, setLoginTriggered] = React.useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = React.useState(false);

  const languageFromSession = sessionStorage.getItem('b2cLoginLang');
  const normalizedI18nLanguage = (i18n.language || 'en').split('-')[0];
  const preferredLanguage = (lang === 'en' || lang === 'fr')
    ? lang
    : (languageFromSession === 'en' || languageFromSession === 'fr')
      ? languageFromSession
      : normalizedI18nLanguage === 'fr'
        ? 'fr'
        : 'en';

  useEffect(() => {
    if (!isLoading) {
      setHasCheckedAuth(true);
    }
  }, [isLoading]);

  // Automatically trigger Azure B2C login popup when user accesses sign-in page without authentication
  // Only trigger after initial auth check is complete
  useEffect(() => {
    if (hasCheckedAuth && !isAuthenticated && !loginTriggered) {
      setLoginTriggered(true);
      // Trigger the Azure B2C login popup immediately
      login().catch((err) => {
        console.error('Auto-login failed:', err);
        // If login fails or is cancelled, stay on this page with message
        setLoginTriggered(false);
      });
    }
  }, [hasCheckedAuth, isAuthenticated, loginTriggered, login]);

  if (isLoading) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
            <p>{t('auth.authenticationStatus')}</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (isAuthenticated && user) {
    sessionStorage.removeItem('b2cLoginLang');
    navigate(buildRouteWithLang(ROUTES.CLIENT_CREDENTIALS, preferredLanguage), { replace: true });
    return null;
  }

  return null;
};

export default SignIn;